/**
 * Timesheet hours – one method for totals.
 * Values stored as hours.minutes (e.g. 1.31 = 1h 31min).
 * Total = sum hours + sum minutes, then 60 min = 1 hour carry. No decimal in the sum.
 */

export type HoursMinutes = { h: number; m: number };

/** Parse hours.minutes (e.g. 1.31) → { h: 1, m: 31 }. */
export function parseHoursMinutes(value: number): HoursMinutes {
  if (value == null || Number.isNaN(value) || value < 0) return { h: 0, m: 0 };
  const h = Math.floor(value);
  const m = Math.round((value - h) * 100);
  return { h, m: Math.max(0, Math.min(59, m)) };
}

/** Single sum: add all (h,m) pairs; 60 min = 1 hour carry. */
function addPairs(pairs: HoursMinutes[]): HoursMinutes {
  if (!pairs?.length) return { h: 0, m: 0 };
  let totalH = 0;
  let totalM = 0;
  pairs.forEach(({ h, m }) => {
    totalH += h;
    totalM += m;
  });
  const carry = Math.floor(totalM / 60);
  return { h: totalH + carry, m: totalM % 60 };
}

/**
 * THE one method for totals. Values in hours.minutes (e.g. 1.31, 2.45).
 * Sum hours, sum minutes, 60 min = 1 hour; remainder minutes.
 */
export function sumHoursMinutes(values: number[]): HoursMinutes {
  if (!values?.length) return { h: 0, m: 0 };
  return addPairs(values.map(parseHoursMinutes));
}

/** Format "Xh Ymin" from (h, m). */
export function formatHoursMinutesFromHM(h: number, m: number): string {
  const H = Math.max(0, Math.floor(Number(h) || 0));
  const M = Math.max(0, Math.min(59, Math.floor(Number(m) || 0)));
  return M === 0 ? `${H}h 0min` : `${H}h ${M}min`;
}

/** hours.minutes → decimal (only when needed for API/display). */
export function hoursMinutesToDecimalHours(value: number): number {
  if (value == null || Number.isNaN(value) || value < 0) return 0;
  const { h, m } = parseHoursMinutes(value);
  return h + m / 60;
}

/** decimal hours → (h, m). */
export function fromDecimalHoursToHM(decimalHours: number): HoursMinutes {
  if (decimalHours == null || Number.isNaN(decimalHours) || decimalHours < 0) return { h: 0, m: 0 };
  const totalMins = Math.round(decimalHours * 60);
  return { h: Math.floor(totalMins / 60), m: totalMins % 60 };
}

/** Sum array of decimal hours in H+M (same logic as sumHoursMinutes). */
export function sumFromDecimalHours(decimalArray: number[]): HoursMinutes {
  if (!decimalArray?.length) return { h: 0, m: 0 };
  return addPairs(decimalArray.map(fromDecimalHoursToHM));
}

/** Format total from decimal array (e.g. row week columns stored as decimal). */
export function formatTotalHoursFromDecimalArray(decimalArray: number[]): string {
  const { h, m } = sumFromDecimalHours(decimalArray);
  return formatHoursMinutesFromHM(h, m);
}

export function convertWeekHoursToDecimal(weekHours: number): number {
  if (weekHours == null || Number.isNaN(weekHours) || weekHours < 0) return 0;
  return weekHours;
}

/** Entry total: date+hours (hours.minutes) or week1–5 (hours.minutes). */
function entryToPairs(entry: {
  date?: Date | string | null;
  hours?: number | null;
  week1Hours?: number;
  week2Hours?: number;
  week3Hours?: number;
  week4Hours?: number;
  week5Hours?: number;
}): HoursMinutes[] {
  const hasDateAndHours = entry.date != null && entry.hours != null && Number(entry.hours) > 0;
  if (hasDateAndHours) return [parseHoursMinutes(entry.hours!)];
  return [
    entry.week1Hours, entry.week2Hours, entry.week3Hours, entry.week4Hours, entry.week5Hours
  ].map(w => parseHoursMinutes(w ?? 0));
}

export function computeEntryTotalDecimalHours(entry: {
  date?: Date | string | null;
  hours?: number | null;
  week1Hours?: number;
  week2Hours?: number;
  week3Hours?: number;
  week4Hours?: number;
  week5Hours?: number;
}): number {
  const { h, m } = addPairs(entryToPairs(entry));
  return h + m / 60;
}

export function getTimesheetTotalHoursMinutes(entries: Array<{
  date?: Date | string | null;
  hours?: number | null;
  week1Hours?: number;
  week2Hours?: number;
  week3Hours?: number;
  week4Hours?: number;
  week5Hours?: number;
}>): { totalHours: number; totalMinutes: number } {
  if (!entries?.length) return { totalHours: 0, totalMinutes: 0 };
  const pairs = entries.flatMap(entryToPairs);
  const { h, m } = addPairs(pairs);
  return { totalHours: h, totalMinutes: m };
}

export function computeTimesheetTotalDecimalHours(entries: Array<{
  date?: Date | string | null;
  hours?: number | null;
  week1Hours?: number;
  week2Hours?: number;
  week3Hours?: number;
  week4Hours?: number;
  week5Hours?: number;
}>): number {
  if (!entries?.length) return 0;
  const { totalHours, totalMinutes } = getTimesheetTotalHoursMinutes(entries);
  return totalHours + totalMinutes / 60;
}

/** Format decimal hours as "Xh Ymin". */
export function formatHoursMinutes(decimalHours: number): string {
  if (decimalHours == null || Number.isNaN(decimalHours) || decimalHours < 0) return '0h 0min';
  const { h, m } = fromDecimalHoursToHM(decimalHours);
  return formatHoursMinutesFromHM(h, m);
}

export function formatTotalHours(decimalHours: number): string {
  return formatHoursMinutes(decimalHours);
}

/** Week of the month (1-5) for a date, matching the buckets the Work Log screen writes. */
export function weekOfMonthForDate(date: string | Date | null | undefined): number {
  if (!date) return 0;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return 0;

  const day = d.getDate();
  if (day >= 1 && day <= 7) return 1;
  if (day >= 8 && day <= 14) return 2;
  if (day >= 15 && day <= 21) return 3;
  if (day >= 22 && day <= 28) return 4;
  if (day >= 29 && day <= 31) return 5;
  return 0;
}

/**
 * One entry's hours split into the five weekly buckets, in hours.minutes form.
 *
 * Entries are stored one of two ways: `week` mode fills week1Hours..week5Hours, while the
 * date-based mode users get by default fills `date` + `hours`. Reports that summed only the
 * weekN columns therefore showed 0h and "Not Entered" for anyone whose rows were date-based,
 * even with dozens of entries logged. Prefer the weekN columns when present, otherwise place
 * `hours` into the bucket its date falls in.
 */
export function entryWeeklyHours(entry: {
  date?: string | Date | null;
  hours?: number;
  week1Hours?: number;
  week2Hours?: number;
  week3Hours?: number;
  week4Hours?: number;
  week5Hours?: number;
}): [number, number, number, number, number] {
  // Same precedence as entryToPairs: a date-mode entry is authoritative when it has both a date and
  // hours, otherwise fall back to the weekN columns. Keeping these in step matters — the Work Log
  // page totals via entryToPairs, so a different order here would make the two screens disagree.
  const hours = entry.hours || 0;
  const hasDateAndHours = entry.date != null && hours > 0;

  if (hasDateAndHours) {
    const weeks: [number, number, number, number, number] = [0, 0, 0, 0, 0];
    const week = weekOfMonthForDate(entry.date);
    if (week >= 1 && week <= 5) {
      weeks[week - 1] = hours;
    }
    return weeks;
  }

  return [
    entry.week1Hours || 0,
    entry.week2Hours || 0,
    entry.week3Hours || 0,
    entry.week4Hours || 0,
    entry.week5Hours || 0,
  ];
}
